import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, Quote, PenLine, X, Loader2 } from "lucide-react";
import { TESTIMONIALS } from "@/data";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReviewCard = ({ review }) => (
  <article
    data-testid="testimonial-card"
    className="flex-1 flex flex-col bg-[#0C1827] border border-[#1E3A2B] rounded-2xl p-6 hover:border-[#F5A623]/50 transition-colors duration-300 select-none"
  >
    <Quote size={24} className="text-[#F5A623]/60 mb-3" />
    <div className="flex gap-0.5 mb-3">
      {[...Array(review.rating)].map((_, i) => (
        <Star key={i} size={14} className="fill-[#F5A623] text-[#F5A623]" />
      ))}
    </div>
    <p className="text-sm text-slate-300 leading-relaxed flex-1">"{review.text}"</p>
    <footer className="mt-5 pt-4 border-t border-[#1E324A]">
      <div className="font-display font-bold text-white text-sm">{review.author}</div>
    </footer>
  </article>
);

const StarPicker = ({ value, onPick }) => (
  <div className="flex gap-1.5" data-testid="review-star-picker">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        data-testid="review-star-button"
        onClick={() => onPick(n)}
        aria-label={`${n} star${n === 1 ? "" : "s"}`}
        className="transition-transform hover:scale-110"
      >
        <Star
          size={30}
          className={
            n <= value ? "fill-[#F5A623] text-[#F5A623]" : "text-slate-600 hover:text-[#F5A623]/60"
          }
        />
      </button>
    ))}
  </div>
);

export const Testimonials = () => {
  const [extraReviews, setExtraReviews] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [rating, setRating] = useState(5);
  const [ratingError, setRatingError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadReviews = async () => {
    try {
      const { data } = await axios.get(`${API}/reviews`);
      setExtraReviews(data);
    } catch {
      // static reviews still show
    }
  };

  useEffect(() => {
    loadReviews();
  }, []);

  const allReviews = [
    ...TESTIMONIALS,
    ...extraReviews.map((r) => ({ author: r.name, text: r.text, rating: r.rating })),
  ];

  const pickStars = (n) => {
    setRating(n);
    setRatingError(
      n === 5
        ? ""
        : "Thanks for your honesty, but this site only accepts 5-star reviews."
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating !== 5) {
      setRatingError("Thanks for your honesty, but this site only accepts 5-star reviews.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, { name, text, rating });
      toast.success("Thank you! Your review will appear once Dan approves it.");
      setModalOpen(false);
      setName("");
      setText("");
      setRating(5);
      loadReviews();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not submit your review.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full bg-[#0C1827] border border-[#1E324A] rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#F5A623]";

  return (
    <section id="reviews" className="bg-[#13261C] py-24" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="font-mono-accent text-xs uppercase tracking-[0.2em] text-[#F5A623]">
            Client Reviews
          </span>
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white mt-3 uppercase">
            Word from the Highlands
          </h2>
          <div className="mt-4 flex items-center justify-center gap-2">
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={18} className="fill-[#F5A623] text-[#F5A623]" />
              ))}
            </div>
            <span className="text-sm text-slate-300">5.0 — every review, every time</span>
          </div>
          <button
            data-testid="leave-review-button"
            onClick={() => setModalOpen(true)}
            className="mt-6 inline-flex items-center gap-2 bg-[#F5A623] text-[#0C1827] font-display font-bold text-sm px-6 py-3 rounded-full hover:bg-[#E8961F] hover:-translate-y-0.5 transition-[background-color,transform] duration-200"
          >
            <PenLine size={15} /> Leave a Review
          </button>
        </div>

        <Carousel opts={{ align: "start", loop: true }} className="w-full" data-testid="reviews-carousel">
          <CarouselContent className="-ml-5 items-stretch">
            {allReviews.map((review, i) => (
              <CarouselItem
                key={`${review.author}-${i}`}
                className="pl-5 sm:basis-1/2 lg:basis-1/3 xl:basis-1/4 flex"
              >
                <ReviewCard review={review} />
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="flex justify-center gap-3 mt-10">
            <CarouselPrevious
              data-testid="reviews-carousel-prev"
              className="static translate-y-0 h-11 w-11 rounded-full border-[#F5A623]/50 bg-transparent text-[#F5A623] hover:bg-[#F5A623] hover:text-[#0C1827]"
            />
            <CarouselNext
              data-testid="reviews-carousel-next"
              className="static translate-y-0 h-11 w-11 rounded-full border-[#F5A623]/50 bg-transparent text-[#F5A623] hover:bg-[#F5A623] hover:text-[#0C1827]"
            />
          </div>
        </Carousel>
      </div>

      {modalOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
          data-testid="review-modal"
          onClick={(e) => e.target === e.currentTarget && setModalOpen(false)}
        >
          <div className="w-full max-w-md bg-[#142234] border border-[#1E324A] rounded-3xl p-8 relative">
            <button
              data-testid="review-modal-close"
              onClick={() => setModalOpen(false)}
              className="absolute top-5 right-5 text-slate-400 hover:text-white"
              aria-label="Close"
            >
              <X size={20} />
            </button>
            <h3 className="font-display font-extrabold text-xl text-white uppercase">
              Leave a Review
            </h3>
            <p className="text-sm text-slate-400 mt-1">
              Had work done by Dan? Share your experience.
            </p>
            <form onSubmit={handleSubmit} className="mt-6 space-y-5">
              <input
                data-testid="review-form-name"
                required
                minLength={2}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your Name *"
                className={inputClass}
              />
              <textarea
                data-testid="review-form-text"
                required
                minLength={10}
                rows={4}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Tell us about the work Dan did for you... *"
                className={`${inputClass} resize-none`}
              />
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
                  Your Rating
                </label>
                <StarPicker value={rating} onPick={pickStars} />
                {ratingError && (
                  <p data-testid="review-rating-error" className="mt-2 text-sm text-red-400">
                    {ratingError}
                  </p>
                )}
              </div>
              <button
                data-testid="review-submit-button"
                type="submit"
                disabled={submitting || rating !== 5}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#F5A623] text-[#0C1827] font-display font-bold px-6 py-3.5 rounded-full hover:bg-[#E8961F] disabled:opacity-50 disabled:cursor-not-allowed transition-[background-color,opacity]"
              >
                {submitting ? <Loader2 size={17} className="animate-spin" /> : null}
                {submitting ? "Publishing..." : "Publish My Review"}
              </button>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};
